import { ArrowTopRightOnSquareIcon, ArrowUturnLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

const GlossaryTerms: React.FC = () => {
  const linkItems = [
    { text: "Definition", icon: ChevronRightIcon, target: "definition" },
    { text: "Access for Payment Services", icon: ChevronRightIcon, target: "access-for-payment-services" },
    { text: "Bank Statements", icon: ChevronRightIcon, target: "bank-statements" },
    { text: "Cash Deposit/Withdrawal", icon: ChevronRightIcon, target: "cash-deposit-withdrawal" },
    { text: "Standing Order", icon: ChevronRightIcon, target: "standing-order" },
    { text: "Debit Card", icon: ChevronRightIcon, target: "debit-card" },
    { text: "Credit Card", icon: ChevronRightIcon, target: "credit-card" },
    { text: "International Card Payments", icon: ChevronRightIcon, target: "international-card-payments" },
    { text: "Local Card Payments", icon: ChevronRightIcon, target: "local-card-payments" },
    { text: "Card Replacement", icon: ChevronRightIcon, target: "card-replacement" },
    { text: "SEPA Transfer", icon: ChevronRightIcon, target: "sepa-transfer" },
    { text: "Non-Euro Transfer", icon: ChevronRightIcon, target: "non-euro-transfer" },
    { text: "Receiving Money in Euros", icon: ChevronRightIcon, target: "receiving-money-in-euros" },
    { text: "Receiving Money in Non-Euro Currencies", icon: ChevronRightIcon, target: "receiving-money-in-non-euro-currencies" },
    { text: "Arranged Overdraft", icon: ChevronRightIcon, target: "arranged-overdraft" },
    { text: "Direct Debit", icon: ChevronRightIcon, target: "direct-debit" },
  ];

  return (
    <div className="flex flex-col items-center container pt-20">
      {/* Header Section */}
      <header className="w-full px-5">
        <h1 className="text-4xl font-extrabold text-gray-900">Glossary of Terms</h1>
        <p className="mt-4 text-lg text-gray-700">This Glossary of Terms was last updated on 24 May 2025.  </p>
      </header>

      {/* Main Content Section */}
      <section className="w-full px-5 mt-8 p-6">
        <p className="text-gray-800">If you have any questions about how we use your personal data, you can get in touch by one of the methods set out in the Contact us section.</p>
      </section>

      {/* Link Section */}
      <div className="w-full mt-8 bg-gray-100 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {linkItems.map((item, index) => {
            const IconComponent = item.icon; // Get the icon component dynamically
            return (
              <a
                key={index}
                href={`#${item.target}`} // Anchor link to the section
                className="flex items-center justify-between bg-white p-4 rounded-lg shadow-md hover:bg-gray-50 transition"
              >
                <span className="text-gray-900 font-medium">{item.text}</span>
                <IconComponent className="h-6 w-6" /> {/* Render the icon dynamically */}
              </a>
            );
          })}
        </div>
      </div>

      <section className="w-full px-5 my-8 scroll-mt-20">
        <div className="space-y-6">
          <div id="definition">
            <p className="font-semibold text-gray-900 text-xl">Definition</p>
            <p className="text-gray-800">The Program Manager or Cobrand operates the account for use by the customer.</p>
          </div>
          <div id="access-for-payment-services">
            <p className="font-semibold text-gray-900 text-xl">Access for Payment Services</p>
            <p className="text-gray-800">The Program Manager or Cobrand provides the customer with access for payment services using the internet, mobile or telephone.</p>
          </div>
          <div id="bank-statements">
            <p className="font-semibold text-gray-900 text-xl">Bank Statements</p>
            <p className="text-gray-800">The customer requests bank statements, in paper or on other Durable Medium, over and above those which the Program Manager or Cobrand is required to send by law.</p>
          </div>
          <div id="cash-deposit-withdrawal">
            <p className="font-semibold text-gray-900 text-xl">Cash Deposit/Withdrawal</p>
            <p className="text-gray-800">The customer requests to deposit or withdraw cash in or from an account.</p>
          </div>
          <div id="standing-order">
            <p className="font-semibold text-gray-900 text-xl">Standing Order</p>
            <p className="text-gray-800">The Program Manager or Cobrand makes regular transfers, on the instruction of the customer, of a fixed amount of money from the customer's account to another account.</p>
          </div>
          <div id="debit-card">
            <p className="font-semibold text-gray-900 text-xl">Debit Card</p>
            <p className="text-gray-800">
              The Program Manager or Cobrand provides a payment card linked to the customer's account, enabling the latter to withdraw and pay, locally, abroad, and even online. The amount of each transaction made using the card is taken directly and
              in full, from the customer's account.
            </p>
          </div>
          <div id="credit-card">
            <p className="font-semibold text-gray-900 text-xl">Credit Card</p>
            <p className="text-gray-800">
              The Program Manager or Cobrand provides a payment card linked to the customer's payment account. The total amount of the transactions made using the card during an agreed period is taken either in full or in part from the customer's
              payment account on an agreed date. A Credit Agreement between the Program Manager or Cobrand and the customer determines whether interest will be charged to the customer for the borrowing.
            </p>
          </div>
          <div id="international-card-payments">
            <p className="font-semibold text-gray-900 text-xl">International Card Payments</p>
            <p className="text-gray-800">The Program Manager or Cobrand enables the customer to make payments using an international debit or credit card.</p>
          </div>
          <div id="local-card-payments">
            <p className="font-semibold text-gray-900 text-xl">Local Card Payments</p>
            <p className="text-gray-800">The Program Manager or Cobrand only enables the customer to make local payments using a local debit or credit card.</p>
          </div>
          <div id="card-replacement">
            <p className="font-semibold text-gray-900 text-xl">Card Replacement</p>
            <p className="text-gray-800">The customer requests a replacement of a card that was lost, stolen or damaged.</p>
          </div>
          <div id="sepa-transfer">
            <p className="font-semibold text-gray-900 text-xl">SEPA Transfer</p>
            <p className="text-gray-800">The Program Manager or Cobrand transfers money, in Euros, on the instruction of the customer, from the customer's account to another account in a SEPA country.</p>
          </div>
          <div id="non-euro-transfer">
            <p className="font-semibold text-gray-900 text-xl">Non-Euro Transfer</p>
            <p className="text-gray-800">The Program Manager or Cobrand transfers money, in non-euro currencies, on the instruction of the customer, from the customer's account to another account.</p>
          </div>
          <div id="receiving-money-in-euros">
            <p className="font-semibold text-gray-900 text-xl">Receiving Money in Euros</p>
            <p className="text-gray-800">The customer receives money in Euros from Euro account.</p>
          </div>
          <div id="receiving-money-in-non-euro-currencies">
            <p className="font-semibold text-gray-900 text-xl">Receiving Money in Non-Euro Currencies</p>
            <p className="text-gray-800">The customer receives money in non-Euro currencies from non-Euro account.</p>
          </div>
          <div id="arranged-overdraft">
            <p className="font-semibold text-gray-900 text-xl">Arranged Overdraft</p>
            <p className="text-gray-800">
              The Program Manager or Cobrand and the customer agree in advance that the customer may borrow money when there is no money left in the account. The agreement determines a maximum amount that can be borrowed, and whether fees and
              interest will be charged to the customer.
            </p>
          </div>
          <div id="direct-debit">
            <p className="font-semibold text-gray-900 text-xl">Direct Debit</p>
            <p className="text-gray-800">
              The customer permits someone else (recipient) to instruct the Program Manager or Cobrand to transfer money from the customer's account to that recipient. The Program Manager or Cobrand then transfers money to the recipient on a date or
              dates agreed by the customer and the recipient. The amount may vary.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlossaryTerms;
